import React, { useState, useRef, useMemo } from "react";
import ExcelUploader from "../common/ExcelUploader";
import ChainList from "../common/ChainList";
import { useApi } from "./../../hooks/useApi";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import LoadingOverlay from "../common/LoadingOverlay";
import DmeLogDetail from "../log-cargas/DmeLogDetail"; // asumo ruta; ajusta si es otra

function findKeyByNames(rowSample = {}, candidates = []) {
    // normaliza y busca la primera key cuyo nombre coincida con alguna candidata
    const norm = (s) => String(s ?? "").toLowerCase().replace(/\s+/g, "");
    const sampleKeys = Object.keys(rowSample || {});
    const lk = sampleKeys.map(k => ({ k, nk: norm(k) }));
    for (const cand of candidates) {
        const nc = norm(cand);
        const found = lk.find(x => x.nk === nc || x.nk.includes(nc) || nc.includes(x.nk));
        if (found) return found.k;
    }
    // fallback: devuelve la primera key que parezca número si buscamos total
    if (candidates.some(c => c.toLowerCase().includes("total") || c.toLowerCase().includes("importe"))) {
        const maybeNumber = lk.find(x => (rowSample[x.k] !== null && rowSample[x.k] !== undefined) &&
            !isNaN(Number(String(rowSample[x.k]).replace(",", "."))));
        return maybeNumber ? maybeNumber.k : null;
    }
    return null;
}

const sumTotalsFromRows = (rows) => {
    if (!rows || rows.length === 0) return { totalAmount: 0, totalRecords: 0, uniqueFolios: 0 };

    // heurística: keys probables
    const totalKey = findKeyByNames(rows[0], ["Total", "total", "Importe", "Amount", "amount", "TotalAmount"]);
    const refKey = findKeyByNames(rows[0], ["Reference", "Referencia", "Ref", "Folio", "folio", "reference"]);
    // sumar
    let totalAmount = 0;
    let totalRecords = rows.length;
    const folios = new Set();
    rows.forEach(r => {
        if (totalKey) {
            const v = r[totalKey];
            if (v !== null && v !== undefined && String(v).trim() !== "") {
                const n = Number(String(v).replace(",", "."));
                if (!isNaN(n)) totalAmount += n;
            }
        }
        if (refKey) {
            const f = String(r[refKey] ?? "").trim();
            if (f !== "") folios.add(f);
        }
    });
    return {
        totalAmount,
        totalRecords,
        uniqueFolios: folios.size,
        totalKey,
        refKey
    };
};

function App() {
    const { get, post } = useApi();
    const { accounts } = useMsal();
    const user = accounts?.[0];
    const [selectedChain, setSelectedChain] = useState("");   // cadena elegida
    const [excelRows, setExcelRows] = useState([]);
    const [excelLayout, setExcelLayout] = useState(null);// filas desde ExcelUploader
    const [isLocked, setIsLocked] = useState(false);
    const [fileName, setFileName] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);                      // 1,2,3
    const [showLogDetail, setShowLogDetail] = useState(false);
    const [logDetailData, setLogDetailData] = useState(null);
    const [uploaderKey, setUploaderKey] = useState(0);
    const fileNameRef = useRef("");

    // ExcelUploader -> onDataLoaded
    const handleDataLoaded = ({ layout, rows, errors, fileName }) => {
        setExcelRows(rows || []);
        setExcelLayout(layout || null);
        setFileName(fileName || "");
        fileNameRef.current = fileName || "";
        if (rows && rows.length > 0 && (!errors || errors.length === 0)) {
            // bloquea selector y permite avanzar
            setIsLocked(true);
        } else {
            setIsLocked(false);
        }
    };

    const handleClear = () => {
        // reinicia wizard y recarga
        setSelectedChain("");
        setExcelRows([]);
        setFileName("");
        fileNameRef.current = "";
        setIsLocked(false);
        setStep(1);
        setExcelLayout(null);
        setShowLogDetail(false);
        setLogDetailData(null);
        setUploaderKey(k => k + 1);
    };

    const goNext = () => {
        if (step === 1) {
            if (!selectedChain) return toast.error("Selecciona una cadena para continuar");
            setStep(2);
        } else if (step === 2) {
            if (!fileNameRef.current || excelRows.length === 0) return toast.error("Carga un archivo válido antes de continuar");
            setStep(3);
        }
    };
    const goPrev = () => setStep((s) => Math.max(1, s - 1));

    const computedTotals = useMemo(() => sumTotalsFromRows(excelRows), [excelRows]);

    const handleExecute = async () => {
        const currentFileName = fileNameRef.current;
        if (!selectedChain) return toast.error("Selecciona una cadena");
        if (!currentFileName) return toast.error("No se detectó nombre de archivo");
        if (!excelRows || excelRows.length === 0) return toast.error("No hay datos para procesar");

        const payload = {
            chain: selectedChain,
            fileName: currentFileName,
            userEmail: user?.username ?? "",
            data: excelRows
        };

        setLoading(true);
        try {
            // 1) Enviar el batch
            const response = await post("enviaDME", payload);
            console.log("enviaDME response:", response);

            // 2) Obtener el returnId (ajusta si tu API usa otra propiedad)
            const returnId = response?.returnId ?? response?.id;
            if (!returnId) {
                console.error("enviaDME no devolvió returnId:", response);
                toast.error("El servicio no devolvió identificador de proceso (returnId).");
                return;
            }

            // 3) Solicitar inmediatamente el detalle procesado
            const detail = await get("DetalleDmeLog", { urlParams: { id: returnId } });
            console.log("DetalleDmeLog response:", detail);

            const detailRows = detail?.rows || detail?.detailRows || [];
            if (!detailRows || detailRows.length === 0) {
                console.error("Servicio DetalleDmeLog no devolvió filas para returnId:", returnId, detail);
                toast.error("No se obtuvo detalle del procesamiento. Revisa el log principal.");
                return;
            }

            // 4) Mostrar detalle en la UI (estructura para DmeLogDetail)
            const dataToShow = {
                fileName: payload.fileName,
                chain: payload.chain,
                date: detail?.date || response?.date || new Date().toISOString(),
                statusText: detail?.statusText || response?.statusText || "Cargado",
                results: detail?.summary || detail?.results || `Procesado: ${detailRows.length}`,
                rows: detailRows,
                returnId
            };

            setLogDetailData(dataToShow);
            setShowLogDetail(true);
            toast.success("Carga procesada.");
        } catch (err) {
            console.error("Error en handleExecute:", err);
            toast.error("Error al ejecutar la carga o al obtener el detalle.");
        } finally {
            setLoading(false);
        }
    };

    /*const handleExecute = async () => {
        const currentFileName = fileNameRef.current;
        if (!selectedChain) return toast.error("Selecciona una cadena");
        if (!currentFileName) return toast.error("No se detectó nombre de archivo");
        if (!excelRows || excelRows.length === 0) return toast.error("No hay datos para procesar");

        const payload = {
            chain: selectedChain,
            fileName: currentFileName,
            userEmail: user?.username ?? "",
            data: excelRows
        };

        setLoading(true);
        try {
            const response = await post("enviaDME", payload);
            // asume response trae info del log. Si no, construye fallback
            if (response && response.returnId) {
                const dataToShow = {
                    fileName: payload.fileName,
                    chain: payload.chain,
                    date: response.date || new Date().toISOString(),
                    statusText: response.statusText || "success",
                    results: response.results || `${response.processed ?? 0} filas procesadas`,
                    rows: response.rows || response.detailRows || [], // si la API devuelve rows
                    returnId: response.returnId
                };
                setLogDetailData(dataToShow);
                setShowLogDetail(true);
                setStep(3);
            } else {
                // fallback: mostrar lo que tenemos localmente como "procesado"
                const fallback = {
                    fileName: payload.fileName,
                    chain: payload.chain,
                    date: new Date().toISOString(),
                    statusText: "success",
                    results: `Folios en archivo: ${computedTotals.uniqueFolios}, Registros: ${computedTotals.totalRecords}`,
                    rows: excelRows.map(r => ({
                        reference: r[computedTotals.refKey] ?? r.Reference ?? r.reference,
                        branch: r.Branch ?? r.branch ?? r.Sucursal ?? r.sucursal,
                        clientQA: r.ClientId ?? r.Cliente ?? r.clientQA,
                        sku: r.Sku ?? r.SKU ?? r.sku,
                        skuQA: r.SkuQA ?? r.skuQA,
                        mu: r.MU ?? r.mu,
                        quantity: r.Quantity ?? r.quantity ?? r.qty,
                        price: r.Price ?? r.price,
                        total: r[computedTotals.totalKey] ?? r.Total ?? r.total,
                        statusText: "success",
                        error: ""
                    })),
                    returnId: null
                };
                setLogDetailData(fallback);
                setShowLogDetail(true);
                setStep(3);
                toast.success("Carga simulada (fallback) — revisa detalle.");
            }
            // opcional: limpiar excelRows si quieres
            // setExcelRows([]);
        } catch (err) {
            console.error(err);
            toast.error("Error al ejecutar la carga");
        } finally {
            setLoading(false);
        }
    };*/

    // render UI por paso
    return (
        <div className="lg:col-span-8 col-span-12">
            {loading && <LoadingOverlay />}
            <h1 className="text-xl font-bold mb-4">Carga DME</h1>

            {/* indicador de pasos */}
            <div className="flex items-center gap-3 mb-6">
                <div className={`px-3 py-1 rounded ${step === 1 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>1. Seleccionar cadena</div>
                <div className={`px-3 py-1 rounded ${step === 2 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>2. Cargar archivo</div>
                <div className={`px-3 py-1 rounded ${step === 3 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>3. Vista previa / Ejecutar</div>
            </div>

            {/* STEP 1: Cadena (tiles) */}
            {step === 1 && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Selecciona una cadena</label>
                    <ChainList
                        userEmail={user?.username}
                        selectedChain={selectedChain}
                        setSelectedChain={setSelectedChain}
                        isLocked={isLocked}
                        tileMode={true}
                    />

                    <div className="mt-4 flex gap-3">
                        <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={goNext}>Siguiente</button>
                        <button className="bg-gray-200 px-4 py-2 rounded" onClick={handleClear}>Cancelar</button>
                    </div>
                </div>
            )}

            {/* STEP 2: Uploader */}
            {step === 2 && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Carga archivo</label>

                    <ExcelUploader
                         key={`uploader-${uploaderKey}`}
                        clientName={selectedChain}
                        onDataLoaded={handleDataLoaded}
                        showPreview={false}
                    />

                    <div className="mt-4 flex gap-3">
                        <button className="bg-gray-200 px-4 py-2 rounded" onClick={goPrev}>Anterior</button>
                        <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={goNext}>Siguiente</button>
                    </div>
                </div>
            )}

            {/* STEP 3: Preview + Totales + Ejecutar */}
            {step === 3 && (
                <div className="mb-6">
                    {showLogDetail && logDetailData ? (
                        <div>
                            <h2 className="text-lg font-semibold mb-2">Resultado del procesamiento</h2>
                            <DmeLogDetail data={logDetailData} hideCloseButton={true} onBack={() => { setShowLogDetail(false); }} />
                            <div className="mt-4">
                                <button className="bg-gray-200 px-4 py-2 rounded" onClick={handleClear}>Nueva carga</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 bg-white rounded shadow">
                                    <div className="text-sm text-gray-500">Archivo</div>
                                    <div className="font-medium">{fileName || "-"}</div>
                                </div>
                                <div className="p-4 bg-white rounded shadow">
                                    <div className="text-sm text-gray-500">Importe total</div>
                                        <div className="font-medium">
                                            {computedTotals.totalAmount != null
                                                ? computedTotals.totalAmount.toLocaleString("es-MX", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })
                                                : computedTotals.totalAmount}
                                        </div>
                                </div>
                                <div className="p-4 bg-white rounded shadow">
                                    <div className="text-sm text-gray-500">Registros / Folios únicos</div>
                                    <div className="font-medium">{computedTotals.totalRecords} registros / {computedTotals.uniqueFolios} folios</div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <ExcelUploader
                                    key={`preview-${uploaderKey}`}
                                    clientName={selectedChain}
                                    showPreview={true}
                                    previewOnly={true}
                                    externalData={{ rows: excelRows, layout: excelLayout, fileName }}
                                />
                            </div>

                            <div className="flex gap-3">
                                <button className="bg-gray-200 px-4 py-2 rounded" onClick={goPrev}>Anterior</button>
                                <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleExecute}>Ejecutar carga</button>
                                <button className="bg-red-600 text-white px-4 py-2 rounded" onClick={handleClear}>Reiniciar datos</button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default App